// =============================================================================
// useSaveDb — React hook that returns an isolated supabase client for the
// duration of a component. Use INSIDE any component whose save mutation
// does multiple sequential supabase calls AND lives on a page with many
// concurrent useQuery hooks.
//
// Why this exists:
//   The shared `supabase` client uses navigator.locks to serialise
//   auth-token reads across concurrent callers. On a busy page (QC modal,
//   Pallets new-form, Inspection submit, etc.) those background reads can
//   stall a save mutation's first DB write for minutes on slower tablets
//   (observed: Samsung Tab S9 FE). See [[isolated-supabase-client-for-busy-pages]].
//
// What this returns:
//   A SupabaseClient instance built with persistSession:false +
//   autoRefreshToken:false and the current JWT baked in via an
//   Authorization header. Never touches navigator.locks. Use it for the
//   save flow's INSERT/UPDATE/UPSERT/storage.upload calls; keep using
//   the main `supabase` client for read queries (useQuery).
//
// Lifecycle:
//   - JWT is captured once on mount via the main client's getSession, with a
//     fallback to the durable stored token when the live one momentarily lacks
//     the active_tenant_id claim (see usableToken — keeps this hook in lock-step
//     with useActiveTenant so the page and the save can't disagree on the
//     tenant). Only when NO token anywhere carries the claim does it return null
//     and the caller fails the save with a "session not ready" message.
//   - JWT refreshes every 5 minutes so long-open modals stay valid.
//     supabase-js auth subscriber catches sign-out events and clears
//     the client.
// =============================================================================

import { useEffect, useMemo, useState } from 'react'
import { type SupabaseClient } from '@supabase/supabase-js'
import { supabase, createIsolatedClient } from '@/lib/supabase'
import { decodeJwtPayload, readStoredSession } from '@/lib/offlineSession'
import { refreshSession } from '@/lib/authRefresh'

const REFRESH_INTERVAL_MS = 5 * 60_000   // 5 min — well under JWT expiry (~1 hr)

// The isolated client bakes ONE JWT in and never re-auths, so it must never be
// built from a token that lacks the active_tenant_id claim: server-side
// current_tenant_id() would resolve NULL and every tenant-scoped write policy
// (`… AND tenant_id = current_tenant_id()`) rejects it with 42501 — even for an
// ADMIN (observed live 2026-06-16 on /farm/receipts). Returns the claim string,
// or null when the token can't safely run a tenant-scoped write.
function tenantClaim(token: string | null): string | null {
  if (!token) return null
  const t = decodeJwtPayload(token)?.active_tenant_id
  return (typeof t === 'string' && t !== 'undefined' && t !== 'null' && t.length > 0) ? t : null
}

// A stored token is only safe to bake into the never-re-authing isolated client
// if it is still unexpired — otherwise every write 401s. 5s skew for clock drift.
function tokenLive(token: string): boolean {
  const exp = decodeJwtPayload(token)?.exp
  return typeof exp === 'number' && exp * 1000 > Date.now() + 5_000
}

// Pick a token that CARRIES active_tenant_id for the save client, preferring the
// live session token but falling back to the durable localStorage token — the
// SAME source useActiveTenant trusts for routing. Without this fallback the page
// (resilient: live-OR-stored claim) and the save (live-only) diverge: the QC
// page renders with a tenant while every save throws "Session not ready" because
// the LIVE token momentarily lacks the claim — a just-rotated auto-refresh token
// lands its custom claims a beat late, or getSession() is contended on
// navigator.locks on a busy QC page and resolves null/stale. Fail-closed:
// returns null only when no token anywhere carries a valid, unexpired claim.
function usableToken(live: string | null): string | null {
  if (tenantClaim(live)) return live
  const stored = readStoredSession()?.access_token ?? null
  return stored && tenantClaim(stored) && tokenLive(stored) ? stored : null
}

// Imperative, lock-free session recovery for the SAVE MOMENT. The hook below
// only re-resolves its token at mount / every 5 min / on auth events, so there
// is a real window where its memoised client is null when Save is tapped: a
// token rotation landed claimless mid-modal, a backgrounded tablet's 5-min
// re-sync interval was throttled and the operator taps Save right after waking,
// or the cold-boot claim window raced mount. Failing the save there is a
// dead-end — the operator loses a half-filled form (on QC: every captured
// photo). Call this as a fallback so the save recovers instead:
//
//   const db = saveDb ?? await ensureSaveDb()
//   if (!db) throw new ValidationError('Session not ready — refresh and try again.')
//   // …then use `db` (not saveDb) for the save's uploads / rpc / writes.
//
// It re-picks a claim-bearing token from storage FIRST (no navigator.locks — the
// same contention dodge the hook makes) and only if none is on hand runs the
// single-flight refresh to mint one, then bakes a fresh isolated client. Same
// fail-closed invariant as the hook: returns null only when no token anywhere
// carries a valid, unexpired active_tenant_id claim — never a claimless/expired
// client (which would 42501 every tenant-scoped write). Builds a fresh client
// each call, which is fine for a one-shot save.
export async function ensureSaveDb(): Promise<SupabaseClient | null> {
  let token = usableToken(null)   // stored claim-bearing token, lock-free
  if (!token) {
    const r = await refreshSession().catch(() => null)
    token = usableToken(r && r.ok ? r.session.access_token : null)
  }
  return token ? createIsolatedClient(token) : null
}

// Resolve the isolated save client or throw — the one-liner every write path
// uses so the `saveDb ?? ensureSaveDb()` + null-check branch lives HERE, not
// inlined into dozens of mutationFns (which pushed each over the coverage/CRAP
// complexity gate). `const db = await requireSaveDb(saveDb)` then `db.rpc(...)`.
export async function requireSaveDb(current: SupabaseClient | null): Promise<SupabaseClient> {
  const db = current ?? await ensureSaveDb()
  if (!db) throw new Error('Session not ready — refresh and try again.')
  return db
}

export function useSaveDb(): SupabaseClient | null {
  const [jwt, setJwt] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    // Read the cached session synchronously-ish (fast path — no
    // navigator.locks contention because we only call this once and
    // every other useQuery on the page hits the same cache).
    const sync = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      let token = usableToken(session?.access_token ?? null)
      // No claim-bearing token on hand — the live token lacked active_tenant_id
      // (cold-boot window before the custom-claims token lands, or a pre-hook
      // token) AND the stored fallback was claimless/expired/absent. Force the
      // single-flight refresh — re-running customize_access_token mints the
      // claim — then re-pick (the refreshed token, or the stored fallback again
      // if the refresh adopted an already-rotated claimless token). Only fires
      // in that rare window; the normal path (live claim present) never refreshes.
      if (!token) {
        const r = await refreshSession().catch(() => null)
        token = usableToken(r && r.ok ? r.session.access_token : null)
      }
      if (!cancelled) setJwt(token)
    }
    void sync()

    // Refresh the captured JWT every 5 min so the isolated client we
    // build below stays valid across a long-open modal session.
    const tick = window.setInterval(() => { void sync() }, REFRESH_INTERVAL_MS)

    // Sign-out → drop the JWT so any in-flight save attempt fails fast.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((evt, session) => {
      if (cancelled) return
      if (evt === 'SIGNED_OUT' || !session) {
        setJwt(null)
      } else {
        // Same resilient pick — a TOKEN_REFRESHED that now carries
        // active_tenant_id makes the client usable again; one that lands without
        // the claim resolves to the stored claim-bearing token instead of
        // nulling the client; a genuinely claimless state keeps it null.
        setJwt(usableToken(session.access_token ?? null))
      }
    })

    return () => {
      cancelled = true
      window.clearInterval(tick)
      subscription.unsubscribe()
    }
  }, [])

  // Memoise the isolated client so identity stays stable while the JWT
  // doesn't change — saves the cost of re-instantiating supabase-js
  // every render. When the JWT refreshes, a fresh client takes over.
  return useMemo(() => jwt ? createIsolatedClient(jwt) : null, [jwt])
}
